import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Permission } from "../../../api/access/accessModel";

interface PermissionLineProps {
  permission: Permission;
  /** Why it can't be switched here, under its name. */
  hint?: string | null;
  locked?: boolean;
  /** The controls on a line of their own beneath the name, for a choice wider than a switch. */
  stacked?: boolean;
  /** The control, given the id of the line's name to label itself with. */
  children: (labelId: string) => ReactNode;
}

/** One permission of a group's screen: its name, a lock where it can't change, and its control. */
export default function PermissionLine({ permission, hint = null, locked = false, stacked = false, children }: PermissionLineProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const name = (
    <span className="min-w-0 flex-1">
      <span id={labelId} className="block text-sm">{t(`access.permissions.${permission}`)}</span>
      {hint === null ? null : <span className="app-text-muted block text-xs">{hint}</span>}
    </span>
  );
  const controls = (
    <>
      {locked ? (
        <span className="shrink-0" title={t("access.locked")}>
          <LockClosedIcon className="app-icon-subtle h-4 w-4" aria-hidden="true" />
          <span className="sr-only">{t("access.locked")}</span>
        </span>
      ) : null}
      {children(labelId)}
    </>
  );
  if (stacked) {
    return (
      <li className="space-y-1 px-4 py-2">
        {name}
        <div className="flex flex-wrap items-center gap-2">{controls}</div>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-3 px-4 py-2">
      {name}
      {controls}
    </li>
  );
}

/** A category's name above its permissions. */
export function CategoryHeading({ children }: { children: ReactNode }) {
  return <li className="app-text-muted px-4 pt-3 pb-1 text-xs font-semibold">{children}</li>;
}
